import React, { useEffect, useMemo, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AppBar, Toolbar, Box, Popper, Paper, ClickAwayListener, MenuList, MenuItem } from "@mui/material";
import logo from "public/Kaleideo-white-logo.png";
import { fetchCurrentUser } from "features/auth/slices/authSlice";
import { getToken } from "lib/auth/authUtils";
import { setDropdownValue } from "features/orders/slices/orderSlice";
import NotificationMenu from "shared/components/NotificationMenu";
import ProfileMenu from "shared/components/ProfileMenu";
import { useIsBhoonidhiUser } from "features/explore/bhoonidhiAccess";
import { useIsInternalUser } from "features/explore/internalAccess";

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const authUser = useSelector((state) => state.auth.user);
  // Bhoonidhi-role users have no tasking, products, or in-app orders (they
  // purchase on the ISRO Bhoonidhi portal) — hide those nav items.
  const isBhoonidhi = useIsBhoonidhiUser();
  // Internal users always get the Products tab (single "Procured Optical
  // Images" card), even when they're also a Bhoonidhi user.
  const isInternal = useIsInternalUser();
  const [ordersMenuOpen, setOrdersMenuOpen] = useState(false);
  const ordersAnchorRef = useRef(null);

  const user = useMemo(
    () => ({
      name: authUser ? `${authUser.first_name} ${authUser.last_name}` : "",
      role: authUser?.role || "",
      initials: authUser ? `${authUser.first_name?.[0] || ""}${authUser.last_name?.[0] || ""}` : "",
    }),
    [authUser],
  );

  useEffect(() => {
    if (!authUser && getToken()) {
      dispatch(fetchCurrentUser());
    }
  }, [dispatch, authUser]);

  // Close orders menu when navigating away
  useEffect(() => {
    setOrdersMenuOpen(false);
  }, [location.pathname]);

  const isOrdersActive = location.pathname === "/orders";

  const getNavLinkStyle = (path) => {
    let isActive = false;
    if (path === "/products") {
      isActive = location.pathname === path || location.pathname.startsWith("/products/");
    } else if (path === "/documentation") {
      isActive = location.pathname === path || location.pathname.startsWith("/documentation/");
    } else if (path === "/explore") {
      isActive = location.pathname === path || location.pathname === "/checkout";
    } else if (path === "/settings") {
      isActive = location.pathname === path || location.pathname === "/settings/user-management";
    } else {
      isActive = location.pathname === path;
    }
    return {
      color: isActive ? "white" : "#2196f3",
      textDecoration: "none",
      fontSize: "16px",
      transition: "color 0.3s ease",
    };
  };

  const getIconButtonStyle = (path) => ({
    color: location.pathname === path ? "white" : "#2196f3",
    transition: "color 0.3s ease",
    "&:hover": { color: "#2196f3", backgroundColor: "rgba(33, 150, 243, 0.08)" },
  });

  const handleOrdersClick = (e) => {
    e.preventDefault();
    setOrdersMenuOpen((prev) => !prev);
  };

  const handleOrdersMenuSelect = (category) => {
    dispatch(setDropdownValue(category));
    setOrdersMenuOpen(false);
    navigate(`/orders?category=${category}`);
  };

  const handleOrdersMenuClose = (event) => {
    if (ordersAnchorRef.current && ordersAnchorRef.current.contains(event.target)) return;
    setOrdersMenuOpen(false);
  };

  // After an order is created, the next visit to Explore should be a fresh
  // hard-reload (clearing all persisted AOI/search/selection state). Otherwise
  // keep the normal soft navigation so in-progress work survives a quick detour.
  const handleExploreNav = (e) => {
    if (sessionStorage.getItem("reloadExploreOnReturn")) {
      e.preventDefault();
      sessionStorage.removeItem("reloadExploreOnReturn");
      window.location = "/explore";
    }
  };

  return (
    <AppBar position="static" sx={{ backgroundColor: "black", padding: "2px 0px" }}>
      <Toolbar
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          minHeight: "30px !important",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 5 }}>
          <Link
            to="/explore"
            style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
            onClick={handleExploreNav}
          >
            <img src={logo} alt="KALEIDEO" style={{ height: "30px", display: "block" }} />
          </Link>
          <Link
            to="/explore"
            style={getNavLinkStyle("/explore")}
            onClick={handleExploreNav}
          >Explore</Link>

          {/* Orders with dropdown — hidden for Bhoonidhi users (they purchase on
              the ISRO Bhoonidhi portal, not through this app's order flow) */}
          {!isBhoonidhi && (
          <Box sx={{ position: "relative" }}>
            <a
              ref={ordersAnchorRef}
              href="/orders"
              onClick={handleOrdersClick}
              style={{
                color: isOrdersActive ? "white" : "#2196f3",
                textDecoration: "none",
                fontSize: "16px",
                transition: "color 0.3s ease",
                cursor: "pointer",
              }}
            >
              Orders
            </a>
            <Popper
              open={ordersMenuOpen}
              anchorEl={ordersAnchorRef.current}
              placement="bottom-start"
              style={{ zIndex: 9999 }}
            >
              <ClickAwayListener onClickAway={handleOrdersMenuClose}>
                <Paper
                  sx={{
                    mt: 0.5,
                    bgcolor: "#222433",
                    border: "1px solid #2F3347",
                    borderRadius: "4px",
                    minWidth: 160,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
                  }}
                >
                  <MenuList dense sx={{ py: 0.5 }}>
                    {[
                      { value: "catalog", label: "Catalog" },
                      { value: "tasking", label: "Tasking" },
                      { value: "drafts", label: " Drafts" },
                    ]
                      .filter((item) => !(isBhoonidhi && item.value === "tasking"))
                      .map((item) => (
                      <MenuItem
                        key={item.value}
                        onClick={() => handleOrdersMenuSelect(item.value)}
                        sx={{
                          color: "#fff",
                          fontFamily: "Poppins, sans-serif",
                          fontSize: 13,
                          py: 0.75,
                          px: 2,
                          "&:hover": { bgcolor: "#2F3347" },
                        }}
                      >
                        {item.label}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Paper>
              </ClickAwayListener>
            </Popper>
          </Box>
          )}

          {/* Internal users see the whole purchased archive here, not a product
              catalogue, so the tab reads "All Images" for them. */}
          {(!isBhoonidhi || isInternal) && (
            <Link to="/products" style={getNavLinkStyle("/products")}>
              {isInternal ? "All Images" : "Products"}
            </Link>
          )}
          <Link to="/documentation" style={getNavLinkStyle("/documentation")}>Documentation</Link>
          {/* Feedback tab — internal-role users only (same gate as My Images). */}
          {isInternal && (
            <Link to="/feedback" style={getNavLinkStyle("/feedback")}>Feedback</Link>
          )}
        </Box>

        <Box sx={{ display: "flex", gap: 2 }}>
          <NotificationMenu iconButtonSx={getIconButtonStyle("/notifications")} />
          <ProfileMenu user={user} iconButtonSx={getIconButtonStyle("/profile")} />
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
